<script setup>
import { useDialogManager } from '@0x234/common-vue/composables/dialog-manager';
import UserProfilePhoto from 'components/UserProfilePhoto.vue';
import AppRoute from 'router/app-route';
import { Route } from 'router/routes';
import { useAuthStore } from 'stores/auth-store';
import { defineAsyncComponent, ref, onUnmounted } from 'vue';

const authStore = useAuthStore();
const dialogManager = useDialogManager();
const isEmailMasked = ref(true);

function openLogoutDialog() {
    dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/UserLogoutDialog.vue')));
}

function maskEmail(email) {
    if (!email || !isEmailMasked.value) {
        return email;
    }
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) {
        return email;
    }
    if (localPart.length <= 2) {
        return localPart[0] + '*' + (localPart.length > 1 ? localPart[localPart.length - 1] : '') + '@' + domain;
    }
    return localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1] + '@' + domain;
}

function toggleEmailMask() {
    isEmailMasked.value = !isEmailMasked.value;
}

onUnmounted(() => {
    isEmailMasked.value = true;
});
</script>

<template>
    <q-list class="user-menu-list">
        <q-item>
            <q-item-section>
                <q-item-label class="text-uppercase" overline>
                    <template v-if="authStore.isUserAuthenticated">
                        Logged in as
                    </template>
                    <template v-else>
                        Welcome, Xprt!
                    </template>
                </q-item-label>
            </q-item-section>
        </q-item>
        <template v-if="authStore.isUserAuthenticated">
            <q-item class="q-pb-md q-pr-xl">
                <q-item-section avatar>
                    <UserProfilePhoto bordered/>
                </q-item-section>
                <q-item-section>
                    <q-item-label class="text-weight-bold">{{ authStore.user.displayName ?? 'New User' }}</q-item-label>
                    <q-item-label caption class="cursor-pointer" @click.capture.stop="toggleEmailMask">
                        {{ maskEmail(authStore.user.email) }}
                    </q-item-label>
                </q-item-section>
            </q-item>
        </template>

        <template v-else>
            <q-item :to="AppRoute.build(Route.LoginPage)" class="q-pr-xl" clickable>
                <q-item-section>
                    <q-item-label class="text-weight-bold text-primary">Login / Register</q-item-label>
                </q-item-section>
            </q-item>
        </template>
        <q-separator/>
        <q-item>
            <q-item-section>
                <q-item-label class="text-uppercase" overline>Searching</q-item-label>
            </q-item-section>
        </q-item>
        <q-item :to="AppRoute.build(Route.ProfileManagerPage)" clickable>
            <q-item-section>
                <q-item-label>My Profile</q-item-label>
            </q-item-section>
        </q-item>
        <q-item :to="AppRoute.build(Route.SearchResultsPage)" clickable>
            <q-item-section>
                <q-item-label>Search Jobs</q-item-label>
            </q-item-section>
        </q-item>
        <template v-if="authStore.isUserAuthenticated">
            <q-item :to="AppRoute.build(Route.ShortlistPage)" clickable>
                <q-item-section>
                    <q-item-label>Saved Jobs</q-item-label>
                </q-item-section>
            </q-item>
            <q-item :to="AppRoute.build(Route.JobApplicationPage)" clickable>
                <q-item-section>
                    <q-item-label>Applied Jobs</q-item-label>
                </q-item-section>
            </q-item>
        </template>
        <q-separator/>
        <q-item>
            <q-item-section>
                <q-item-label class="text-uppercase" overline>Recruiting</q-item-label>
            </q-item-section>
        </q-item>
        <q-item :to="AppRoute.build(Route.JobManagerWizardPage)" clickable>
            <q-item-section>
                <q-item-label>Post a Job</q-item-label>
            </q-item-section>
        </q-item>
        <template v-if="authStore.user?.totalJobs > 0">
            <q-item :to="AppRoute.build(Route.CompanyManagerPage)" clickable>
                <q-item-section>
                    <q-item-label>My Companies</q-item-label>
                </q-item-section>
            </q-item>
            <q-item :to="AppRoute.build(Route.JobManagerListPage)" clickable>
                <q-item-section>
                    <q-item-label>My Jobs</q-item-label>
                </q-item-section>
            </q-item>
        </template>
        <template v-if="authStore.isUserAuthenticated">
            <q-separator/>
            <q-item>
                <q-item-section>
                    <q-item-label class="text-uppercase" overline>Account</q-item-label>
                </q-item-section>
            </q-item>
            <q-item :to="AppRoute.build(Route.AccountSecurityPage)" clickable>
                <q-item-section>
                    <q-item-label>Security</q-item-label>
                </q-item-section>
            </q-item>
            <q-item :to="AppRoute.build(Route.AccountSettingsPage)" clickable>
                <q-item-section>
                    <q-item-label>Settings</q-item-label>
                </q-item-section>
            </q-item>
            <q-separator/>
            <q-item @click="openLogoutDialog()" clickable>
                <q-item-section>
                    <q-item-label class="text-weight-bold text-negative">LOG OUT</q-item-label>
                </q-item-section>
            </q-item>
        </template>
    </q-list>
</template>

<style scoped lang="scss">
.q-list {
    min-width: 250px;

    .q-item {
        background: none;
        border-radius: 0;

        &:first-child {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        &:last-child {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
        }
    }
}
</style>
